import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RouteIcon from '@mui/icons-material/Route';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddRoadIcon from '@mui/icons-material/AddRoad';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import {
  Area,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { reportAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { StatCard } from '../components/ui/StatCard';
import { DashboardFilterBar } from '../components/dashboard/DashboardFilterBar';
import { VehicleDocumentsWidget } from '../components/documents/VehicleDocumentsWidget';
import {
  tableHeadRowSx,
  tableHeadCellSx,
  tableHeadTextSx,
  tableBodyCellSx,
  tableWrapperSx,
  tableRowHoverSx,
} from '../theme/tableStyles';
import { brandColors, APP_FONT_FAMILY, TYPE } from '../theme/appTheme';
import { useAppTheme } from '../contexts/ThemeContext';
import {
  getChartColors,
  getDateRangeFromPreset,
  getModuleIcon,
  getModuleMeta,
  STATUS_OPTIONS,
} from '../constants/dashboardModules';

const CARD_RADIUS = '14px';

const DATE_PRESETS = [
  { id: 'today', label: 'Today', days: 0 },
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: 'all', label: 'All time', days: -1 },
  { id: 'custom', label: 'Custom', days: null as number | null },
] as const;

const fadeSlideUp = (delay = 0) => ({
  animation: 'dashFadeSlideUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both',
  animationDelay: `${delay}s`,
  '@keyframes dashFadeSlideUp': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
});

type DisplayRow = {
  id: string;
  primary: string;
  secondary?: string;
  status?: string;
  amount?: number;
  date?: string | Date | null;
};

type ModuleBlock = {
  key: string;
  label: string;
  type?: string;
  group?: string;
  total: number;
  revenue?: number;
  rows: DisplayRow[];
};

type DashboardPayload = {
  summary?: {
    totalRecords: number;
    totalRevenue: number;
    moduleCount: number;
    activeModules: number;
  };
  modules?: Record<string, ModuleBlock>;
  charts?: {
    tripStatus?: Array<{ name: string; value: number }>;
    revenueTrend?: Array<{ month: string; count: number; amount: number }>;
    activityTrend?: Array<{ month: string; count: number; amount: number }>;
  };
};

function statusChipSx(status?: string, brandPrimary?: string, brandDark?: string) {
  const v = String(status || 'active').toLowerCase();
  const colors: Record<string, { bg: string; color: string }> = {
    active: { bg: `${brandPrimary || '#6221d6'}22`, color: brandDark || '#4c1aa8' },
    completed: { bg: '#dcfce7', color: '#15803d' },
    paid: { bg: '#dcfce7', color: '#15803d' },
    ongoing: { bg: '#dbeafe', color: '#1d4ed8' },
    in_progress: { bg: '#dbeafe', color: '#1d4ed8' },
    assigned: { bg: '#e0e7ff', color: '#4338ca' },
    pending: { bg: '#ffedd5', color: '#c2410c' },
    scheduled: { bg: `${brandPrimary || '#6221d6'}18`, color: brandDark || '#4c1aa8' },
    cancelled: { bg: '#fee2e2', color: '#dc2626' },
    inactive: { bg: '#fee2e2', color: '#dc2626' },
  };
  const c = colors[v] || colors.active;
  return {
    bgcolor: c.bg,
    color: c.color,
    height: 24,
    fontSize: 11,
    fontWeight: 600,
    borderRadius: '999px',
    textTransform: 'capitalize' as const,
  };
}

function parseRoute(secondary?: string): { from: string; to: string } {
  if (!secondary) return { from: '—', to: '—' };
  const parts = secondary.split(/\s*[→\-–—>|]+\s*/);
  if (parts.length >= 2) return { from: parts[0].trim() || '—', to: parts.slice(1).join(' → ').trim() || '—' };
  return { from: secondary, to: '—' };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useLocale();
  const { user } = useAuth();
  const { brand } = useAppTheme();
  const secondary = user?.company?.theme?.secondaryColor || brandColors.info;

  const CHART_COLORS = useMemo(
    () => getChartColors(brand.primary, brand.primaryDark, brand.primaryLight),
    [brand.primary, brand.primaryDark, brand.primaryLight],
  );

  const [datePreset, setDatePreset] = useState<string>('30d');
  const [fromDate, setFromDate] = useState(() => getDateRangeFromPreset(30).fromDate);
  const [toDate, setToDate] = useState(() => getDateRangeFromPreset(30).toDate);
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (moduleFilter !== 'all') params.moduleKey = moduleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await reportAPI.dashboard(params);
      setData(res.data as DashboardPayload);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, moduleFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreset = (presetId: string, days: number | null) => {
    setDatePreset(presetId);
    if (days === null) return;
    const range = getDateRangeFromPreset(days);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  };

  const clearFilters = () => {
    setDatePreset('30d');
    const range = getDateRangeFromPreset(30);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
    setModuleFilter('all');
    setStatusFilter('all');
  };

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (datePreset !== '30d') n += 1;
    if (moduleFilter !== 'all') n += 1;
    if (statusFilter !== 'all') n += 1;
    return n;
  }, [datePreset, moduleFilter, statusFilter]);

  const summary = data?.summary;
  const moduleList = useMemo(() => {
    if (!data?.modules) return [];
    return Object.values(data.modules).sort((a, b) => b.total - a.total);
  }, [data]);

  const moduleOptions = useMemo(
    () => moduleList.map((m) => ({ key: m.key, label: m.label })),
    [moduleList],
  );

  const tripsModule = data?.modules?.trips;
  const vehiclesModule = data?.modules?.vehicles;
  const invoicesModule = data?.modules?.invoices;
  const tripRows = useMemo(() => tripsModule?.rows ?? [], [tripsModule]);
  const recentTrips = useMemo(() => tripRows.slice(0, 8), [tripRows]);

  const upcomingTrips = useMemo(() => {
    const upcomingStatuses = new Set(['pending', 'scheduled', 'assigned', 'created', 'ongoing', 'in_progress']);
    const sorted = [...tripRows].sort((a, b) => {
      const da = a.date ? new Date(String(a.date)).getTime() : 0;
      const db = b.date ? new Date(String(b.date)).getTime() : 0;
      return db - da;
    });
    const filtered = sorted.filter((r) => upcomingStatuses.has(String(r.status || '').toLowerCase()));
    return (filtered.length ? filtered : sorted).slice(0, 5);
  }, [tripRows]);

  const overviewSeries = useMemo(() => {
    const rev = data?.charts?.revenueTrend ?? [];
    const act = data?.charts?.activityTrend ?? [];
    const map = new Map<string, { label: string; revenue: number; trips: number }>();
    rev.forEach((p) => {
      map.set(p.month, { label: p.month, revenue: p.amount, trips: p.count });
    });
    act.forEach((p) => {
      const prev = map.get(p.month) || { label: p.month, revenue: 0, trips: 0 };
      map.set(p.month, { ...prev, trips: p.count });
    });
    return Array.from(map.values());
  }, [data]);

  const tripsByStatus = useMemo(
    () => (data?.charts?.tripStatus ?? []).map((p) => ({ label: p.name, value: p.value })),
    [data],
  );

  const tripStatusTotal = useMemo(
    () => tripsByStatus.reduce((s, x) => s + (x.value || 0), 0),
    [tripsByStatus],
  );

  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timeLabel = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const openModule = (key: string, type?: string) => {
    navigate(getModuleMeta(key, undefined, type).path);
  };

  const kpiCards = [
    {
      title: 'Total Trips',
      value: String(tripsModule?.total ?? summary?.totalRecords ?? 0),
      color: brand.primary,
      bg: brand.green50,
      icon: <RouteIcon />,
      trend: 12,
    },
    {
      title: 'Active Vehicles',
      value: String(vehiclesModule?.total ?? 0),
      color: secondary,
      bg: `${secondary}18`,
      icon: <DirectionsCarIcon />,
      trend: 8,
    },
    {
      title: 'Total Invoices',
      value: String(invoicesModule?.total ?? 0),
      color: '#d97706',
      bg: '#fef3c7',
      icon: <ReceiptLongIcon />,
      trend: 5,
    },
    {
      title: 'Wallet Balance',
      value: formatCurrency(summary?.totalRevenue ?? 0),
      color: brand.accent,
      bg: `${brand.accent}18`,
      icon: <AccountBalanceWalletIcon />,
      trend: 20,
    },
  ];

  const quickActions = [
    {
      title: 'Book a New Trip',
      desc: 'Create & assign a trip',
      icon: <AddRoadIcon />,
      color: brand.primary,
      bg: brand.green50,
      path: getModuleMeta('trips').path,
    },
    {
      title: 'View Invoices',
      desc: 'Billing & payments',
      icon: <ReceiptIcon />,
      color: '#d97706',
      bg: '#fef3c7',
      path: getModuleMeta('invoices').path,
    },
    {
      title: 'Manage Vehicles',
      desc: 'Fleet & compliance',
      icon: <LocalShippingIcon />,
      color: secondary,
      bg: `${secondary}18`,
      path: getModuleMeta('vehicles').path,
    },
    {
      title: 'Support',
      desc: 'Help & documentation',
      icon: <SupportAgentIcon />,
      color: brand.accent,
      bg: `${brand.accent}18`,
      path: '/documentation',
    },
  ];

  const cardSx = {
    elevation: 0,
    borderRadius: CARD_RADIUS,
    border: `1px solid ${brandColors.border}`,
    bgcolor: '#fff',
    boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
  } as const;

  return (
    <Box
      sx={{
        pb: { xs: 2, md: 3 },
        minHeight: '100%',
        mx: { xs: -1.25, sm: -2, md: -2.5 },
        px: { xs: 1.5, sm: 2, md: 2.5 },
        pt: 0,
        background: `linear-gradient(180deg, ${brand.green50} 0%, #f8fafc 38%, #f8fafc 100%)`,
      }}
    >
      {/* Top strip — title + live date/time */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 25,
          mx: { xs: -1.5, sm: -2, md: -2.5 },
          mb: 2,
          px: { xs: 1.5, sm: 2, md: 2.5 },
          py: 1.25,
          borderRadius: 0,
          borderBottom: `1px solid ${brand.primary}18`,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(14px)',
          boxShadow: '0 8px 24px rgba(15,23,42,0.04)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          ...fadeSlideUp(0),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: brand.gradientPrimary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              flexShrink: 0,
              boxShadow: `0 6px 16px ${brand.primary}33`,
            }}
          >
            <SpaceDashboardIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: APP_FONT_FAMILY,
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 700,
                lineHeight: 1.2,
                color: brandColors.textDark,
              }}
            >
              Dashboard
            </Typography>
            <Typography sx={{ fontSize: 12, color: brandColors.textMuted, display: { xs: 'none', sm: 'block' } }}>
              Live overview across all modules
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: '12px',
            bgcolor: brand.green50,
            border: `1px solid ${brand.primary}18`,
            minWidth: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'space-between', sm: 'flex-start' },
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              bgcolor: '#fff',
              color: brand.primary,
              display: 'grid',
              placeItems: 'center',
              boxShadow: `0 2px 8px ${brand.primary}18`,
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ lineHeight: 1.2, mr: 0.5 }}>
            <Typography sx={{ fontSize: 11, color: brandColors.textMuted, fontWeight: 500 }}>{dateLabel}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: brandColors.textDark, fontVariantNumeric: 'tabular-nums' }}>
              {timeLabel}
            </Typography>
          </Box>
          <Tooltip title="Refresh dashboard">
            <IconButton
              size="small"
              onClick={load}
              disabled={loading}
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${brandColors.border}`,
                '&:hover': { bgcolor: brand.green100 },
              }}
            >
              {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <DashboardFilterBar
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={(v) => { setFromDate(v); setDatePreset('custom'); }}
        onToDateChange={(v) => { setToDate(v); setDatePreset('custom'); }}
        datePresets={DATE_PRESETS.map((p) => ({ id: p.id, label: p.label, days: p.days }))}
        activePresetId={datePreset}
        onPresetClick={(p) => handlePreset(p.id, p.days)}
        onApply={load}
        onReset={clearFilters}
        loading={loading}
        activeFilterCount={activeFiltersCount}
      >
        <FormControl size="small" sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: '4px' } }}>
          <InputLabel sx={{ fontSize: 13, fontWeight: 500 }}>Module</InputLabel>
          <Select label="Module" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} sx={{ fontSize: 13 }}>
            <MenuItem value="all">All modules</MenuItem>
            {moduleOptions.map((m) => (
              <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: '4px' } }}>
          <InputLabel sx={{ fontSize: 13, fontWeight: 500 }}>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ fontSize: 13 }}>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DashboardFilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: CARD_RADIUS }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KPI cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 1.75,
          mb: 2.5,
        }}
      >
        {loading && !data
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={118} sx={{ borderRadius: CARD_RADIUS }} />
            ))
          : kpiCards.map((card, i) => (
              <Box key={card.title} sx={fadeSlideUp(0.05 + i * 0.05)}>
                <StatCard
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  color={card.color}
                  bg={card.bg}
                  trend={card.trend}
                  trendLabel="vs last 30 days"
                  compact
                />
              </Box>
            ))}
      </Box>

      {/* All modules */}
      <Box sx={{ mb: 2.5, ...fadeSlideUp(0.15) }}>
        <Typography
          sx={{
            mb: 1.25,
            fontWeight: 600,
            fontSize: TYPE.cardHeading.fontSize,
            lineHeight: TYPE.cardHeading.lineHeight,
            color: brandColors.textDark,
          }}
        >
          All Modules
        </Typography>
        {loading && !data ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 1.25 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: CARD_RADIUS }} />
            ))}
          </Box>
        ) : moduleList.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>No modules available for this range</Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)', xl: 'repeat(6, 1fr)' },
              gap: 1.25,
            }}
          >
            {moduleList.map((mod, i) => {
              const meta = getModuleMeta(mod.key, mod.label, mod.type);
              const Icon = getModuleIcon(meta.icon);
              const accent = brand.primary;
              return (
                <Paper
                  key={mod.key}
                  elevation={0}
                  onClick={() => openModule(mod.key, mod.type)}
                  sx={{
                    ...cardSx,
                    p: 1.5,
                    cursor: 'pointer',
                    animation: 'dashChipIn 0.35s ease both',
                    animationDelay: `${0.05 + i * 0.03}s`,
                    '@keyframes dashChipIn': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                    '&:hover': {
                      borderColor: `${accent}66`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 22px ${accent}22`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: brand.green50,
                        color: accent,
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: brandColors.textDark }}>
                        {mod.label}
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: accent, lineHeight: 1.2 }}>
                        {mod.total}
                      </Typography>
                      {mod.revenue ? (
                        <Typography sx={{ fontSize: 11, color: brandColors.textMuted }}>
                          {formatCurrency(mod.revenue)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Charts row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 1.75,
          mb: 2.5,
          ...fadeSlideUp(0.2),
        }}
      >
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.75, md: 2.25 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: TYPE.cardHeading.fontSize, lineHeight: TYPE.cardHeading.lineHeight, color: brandColors.textDark }}>
                Trip & Revenue Overview
              </Typography>
              <Typography sx={{ fontSize: TYPE.helper.fontSize, color: brandColors.textMuted }}>
                Filtered by the date range above
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                p: 0.4,
                gap: 0.35,
                borderRadius: '10px',
                bgcolor: brand.green50,
                border: `1px solid ${brand.primary}14`,
              }}
            >
              {DATE_PRESETS.filter((r) => r.days !== null && r.days > 0).slice(0, 4).map((r) => (
                <Button
                  key={r.id}
                  size="small"
                  onClick={() => handlePreset(r.id, r.days)}
                  sx={{
                    minWidth: 44,
                    minHeight: 32,
                    height: 32,
                    px: 1.25,
                    borderRadius: '8px',
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'none',
                    color: datePreset === r.id ? '#fff' : brandColors.textMuted,
                    bgcolor: datePreset === r.id ? brand.primary : 'transparent',
                    boxShadow: datePreset === r.id ? `0 4px 12px ${brand.primary}40` : 'none',
                    '&:hover': {
                      bgcolor: datePreset === r.id ? brand.primaryDark : `${brand.primary}12`,
                    },
                  }}
                >
                  {r.id === '7d' ? '7D' : r.id === '30d' ? '30D' : r.id === '90d' ? '3M' : r.label}
                </Button>
              ))}
            </Box>
          </Box>

          {loading && !data ? (
            <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2 }} />
          ) : overviewSeries.length === 0 ? (
            <Box sx={{ height: 260, display: 'grid', placeItems: 'center' }}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>No trend data for this range</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={overviewSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="dashRevFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={brand.primary} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={brand.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: brandColors.textMuted }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: brandColors.textMuted }} axisLine={false} tickLine={false} width={48} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: brandColors.textMuted }} axisLine={false} tickLine={false} width={36} />
                <RechartsTooltip
                  formatter={(value, name) => {
                    const n = Number(value ?? 0);
                    return name === 'revenue' ? formatCurrency(n) : n;
                  }}
                />
                <Legend verticalAlign="top" align="left" height={28} iconType="circle" wrapperStyle={{ fontSize: 12, paddingBottom: 4 }} />
                <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke={brand.primary} fill="url(#dashRevFill)" strokeWidth={2.5} />
                <Line yAxisId="right" type="monotone" dataKey="trips" name="Trips" stroke={secondary} strokeWidth={2.5} dot={{ r: 3, fill: secondary }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.75, md: 2.25 } }}>
          <Typography sx={{ fontWeight: 600, fontSize: TYPE.cardHeading.fontSize, lineHeight: TYPE.cardHeading.lineHeight, color: brandColors.textDark }}>
            Trip Status
          </Typography>
          <Typography sx={{ fontSize: TYPE.helper.fontSize, color: brandColors.textMuted, mb: 1 }}>
            Distribution across your fleet
          </Typography>

          {loading && !data ? (
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
          ) : tripsByStatus.length === 0 ? (
            <Box sx={{ height: 240, display: 'grid', placeItems: 'center' }}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>No trip status data</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', lg: 'column', xl: 'row' }, alignItems: 'center', gap: 1.5, mt: 1 }}>
              <Box sx={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={tripsByStatus} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={58} outerRadius={78} paddingAngle={3} stroke="none">
                      {tripsByStatus.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
                  <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: brandColors.textDark, lineHeight: 1.1 }}>
                      {tripStatusTotal}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: brandColors.textMuted, fontWeight: 500 }}>Total Trips</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tripsByStatus.map((item, i) => {
                  const pct = tripStatusTotal ? Math.round((item.value / tripStatusTotal) * 100) : 0;
                  const color = CHART_COLORS[i % CHART_COLORS.length];
                  return (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: '10px', bgcolor: `${color}12` }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 500, color: brandColors.textDark }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: brandColors.textMuted }}>{pct}%</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Quick actions */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2.5,
          ...fadeSlideUp(0.28),
        }}
      >
        {quickActions.map((action) => (
          <Paper
            key={action.title}
            elevation={0}
            onClick={() => navigate(action.path)}
            sx={{
              ...cardSx,
              p: 1.75,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 10px 28px ${action.color}22`,
                borderColor: `${action.color}44`,
              },
            }}
          >
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: action.bg, color: action.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {action.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: brandColors.textDark, lineHeight: '20px' }}>{action.title}</Typography>
              <Typography sx={{ fontSize: 12, color: brandColors.textMuted, lineHeight: '18px' }}>{action.desc}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <VehicleDocumentsWidget />
      </Box>

      {/* Recent + Upcoming */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 1.75,
          mb: 1,
          ...fadeSlideUp(0.35),
        }}
      >
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.5, md: 2 }, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
            <Typography sx={{ flex: 1, fontWeight: 600, fontSize: TYPE.cardHeading.fontSize, color: brandColors.textDark }}>
              Recent Trips
            </Typography>
            <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => navigate(getModuleMeta('trips').path)} sx={{ textTransform: 'none', fontWeight: 600, color: brand.primary }}>
              View all
            </Button>
          </Box>

          <Box sx={{ ...tableWrapperSx, maxHeight: 360, border: 'none', borderRadius: 0 }}>
            {loading && !data ? (
              <Skeleton variant="rounded" height={220} />
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    {['Trip ID', 'From', 'To', 'Status', 'Date'].map((h) => (
                      <TableCell key={h} sx={tableHeadCellSx()}>
                        <Typography sx={tableHeadTextSx}>{h}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTrips.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ ...tableBodyCellSx, textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        No recent trips
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentTrips.map((row, i) => {
                      const route = parseRoute(row.secondary);
                      return (
                        <TableRow key={row.id || i} sx={tableRowHoverSx}>
                          <TableCell sx={tableBodyCellSx}>
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{row.primary || row.id}</Typography>
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>{route.from}</TableCell>
                          <TableCell sx={tableBodyCellSx}>{route.to}</TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <Chip label={String(row.status || 'active').replace(/_/g, ' ')} size="small" sx={statusChipSx(row.status, brand.primary, brand.primaryDark)} />
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            {row.date ? new Date(String(row.date)).toLocaleDateString() : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.75, md: 2.25 } }}>
          <Typography sx={{ fontWeight: 600, fontSize: TYPE.cardHeading.fontSize, color: brandColors.textDark, mb: 1.5 }}>
            Upcoming Trips
          </Typography>

          {loading && !data ? (
            <Skeleton variant="rounded" height={220} />
          ) : upcomingTrips.length === 0 ? (
            <Typography color="text.secondary" sx={{ fontSize: 13, py: 4, textAlign: 'center' }}>
              No upcoming trips
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {upcomingTrips.map((row, i) => {
                const route = parseRoute(row.secondary);
                const routeLabel = row.secondary || `${route.from} → ${route.to}`;
                return (
                  <Box
                    key={row.id || i}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      p: 1.25,
                      borderRadius: '12px',
                      bgcolor: brand.green50,
                      border: `1px solid ${brand.primary}12`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: '#fff',
                        color: brand.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: `0 2px 8px ${brand.primary}20`,
                      }}
                    >
                      <RouteIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: brandColors.textDark }}>
                        {routeLabel}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: brandColors.textMuted, mt: 0.25 }}>
                        {row.date
                          ? new Date(String(row.date)).toLocaleString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : row.primary}
                      </Typography>
                    </Box>
                    <Chip label={String(row.status || 'pending').replace(/_/g, ' ')} size="small" sx={statusChipSx(row.status, brand.primary, brand.primaryDark)} />
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
export { Dashboard };
